import React from "react";
import { useShallow } from "zustand/react/shallow";
import { MetaAddButton } from "@/components/main/datasets/DatasetMetaSection";
import {
  useOrgContextStore,
  visibleSpaces,
} from "@/components/main/stores/orgContextStore";

interface CreateModelStripProps {
  /// Org-wide, not the filtered bucket: the button only pulses for an account
  /// that has never made a model.
  hasModels: boolean;
  /// The naming overlay is open, so the button reads as pressed.
  isNaming: boolean;
  onNewModel: () => void;
}

/// The Overview's create action. It opens the naming overlay; where the model
/// goes is the overlay's footer, which asks when no space is focused.
export const CreateModelStrip: React.FC<CreateModelStripProps> = ({
  hasModels,
  isNaming,
  onNewModel,
}) => {
  const { activeOrg, hasSpaces } = useOrgContextStore(
    useShallow((s) => ({
      activeOrg: s.activeOrg(),
      hasSpaces: visibleSpaces(s.spaces).length > 0,
    })),
  );
  // The door refuses without a live space, so the affordance says so instead
  // of failing after the click. An org on its way out takes nothing new.
  const refusalReason = activeOrg?.deletion_scheduled_at
    ? `${activeOrg.name} is scheduled for deletion — it takes no new models until that is cancelled.`
    : hasSpaces
      ? undefined
      : "Create a space first — models live in spaces.";

  return (
    <div className="flex items-center justify-end gap-2 mt-8">
      <span
        className={`flex${!hasModels && !refusalReason ? " pulse-hint" : ""}`}
      >
        <MetaAddButton
          label="New model"
          className="naming-opener"
          onClick={onNewModel}
          disabled={!!refusalReason}
          title={refusalReason}
          ariaHasPopup="dialog"
          ariaExpanded={isNaming}
        />
      </span>
    </div>
  );
};
